import { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Search, X, Image as ImageIcon, Download, Trash2, Sparkles, CheckCircle2, User, Camera, Upload, FolderOpen, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import WebcamCapture from '../../WebcamCapture';

const PhotosPanel = ({
  gallery,
  photos,
  totalPhotoCount,
  loading,
  loadingMore,
  hasMore,
  loadMorePhotos,
  onDeletePhoto,
  onSetFeatured,
  onRefresh,
  allTags,
  categorizedTags,
  collectionFilter = 'all',
  onCollectionFilterChange,
  axiosConfig,
  backendUrl,
  galleryId,
  isCrew = false
}) => {
  // Search and filter state
  const [activeTab, setActiveTab] = useState('all');
  const [filenameSearch, setFilenameSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  // Filename search hits fetched from the server (null = search inactive).
  // The photo list is paginated 50 at a time, so filtering only the loaded
  // pages silently misses most of a big gallery — the server sees them all.
  const [serverSearchResults, setServerSearchResults] = useState(null);

  useEffect(() => {
    const term = filenameSearch.trim();
    if (!term) { setServerSearchResults(null); return; }
    let stale = false;
    const timer = setTimeout(async () => {
      try {
        const collParam = collectionFilter && collectionFilter !== 'all'
          ? `&collection=${encodeURIComponent(collectionFilter)}` : '';
        const res = await axios.get(
          `${backendUrl}/api/galleries/${galleryId}/photos?limit=500&search=${encodeURIComponent(term)}${collParam}`,
          axiosConfig
        );
        if (!stale) setServerSearchResults(res.data);
      } catch (e) {
        if (!stale) setServerSearchResults(null); // fall back to loaded-page filter
      }
    }, 300);
    return () => { stale = true; clearTimeout(timer); };
  }, [filenameSearch, collectionFilter, backendUrl, galleryId]);

  // Multi-select state
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveTargetCollection, setMoveTargetCollection] = useState('');

  // Lightbox state
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // AI Tags modal
  const [selectedPhotoTags, setSelectedPhotoTags] = useState(null);

  // Tag search state
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchResults, setSearchResults] = useState([]);

  // Face search state
  const [showWebcam, setShowWebcam] = useState(false);
  const [faceSearchFile, setFaceSearchFile] = useState(null);
  const [faceSearching, setFaceSearching] = useState(false);
  const [faceSearchResults, setFaceSearchResults] = useState([]);

  const collections = gallery?.collections || [];

  // Filter and sort photos (collection filter is now server-side)
  const displayPhotos = useMemo(() => {
    let result;

    if (activeTab === 'tags' && searchResults.length > 0) {
      result = searchResults;
    } else if (activeTab === 'faces' && faceSearchResults.length > 0) {
      result = faceSearchResults.map(r => r.photo || r);
    } else if (filenameSearch.trim() && serverSearchResults !== null) {
      result = [...serverSearchResults];
    } else {
      result = [...photos];
    }

    // Apply filename filter
    if (filenameSearch.trim()) {
      const search = filenameSearch.toLowerCase();
      result = result.filter(photo =>
        photo.filename?.toLowerCase().includes(search)
      );
    }

    // Apply sort - use image_created_at (EXIF date when photo was taken) first, fallback to uploaded_at
    result.sort((a, b) => {
      const dateA = new Date(a.image_created_at || a.created_at || a.uploaded_at || 0);
      const dateB = new Date(b.image_created_at || b.created_at || b.uploaded_at || 0);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [photos, filenameSearch, serverSearchResults, sortOrder, activeTab, searchResults, faceSearchResults]);

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSearchByTags = async () => {
    const searchTerms = selectedTags.join(',');

    if (!searchTerms && selectedTags.length === 0) {
      setSearchResults([]);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('gallery_id', galleryId);
      formData.append('tags', searchTerms);

      if (selectedCategory !== 'all') {
        formData.append('category', selectedCategory);
      }

      const response = await axios.post(
        `${backendUrl}/api/search/by-tags`,
        formData,
        axiosConfig
      );

      setSearchResults(response.data.photos);
      toast.success(`Found ${response.data.total} matching photos`);
    } catch (error) {
      toast.error('Tag search failed');
    }
  };

  const handleFaceSearch = async () => {
    if (!faceSearchFile) {
      toast.error('Please upload a reference photo or take a selfie');
      return;
    }

    setFaceSearching(true);
    try {
      const formData = new FormData();
      formData.append('gallery_id', galleryId);
      formData.append('file', faceSearchFile);

      const response = await axios.post(
        `${backendUrl}/api/search/faces`,
        formData,
        axiosConfig
      );

      const matches = response.data.matches || response.data || [];
      const matchCount = response.data.total || matches.length || 0;

      setFaceSearchResults(matches);

      if (matchCount === 0) {
        toast.error('No matching faces found in this gallery');
      } else {
        toast.success(`Found ${matchCount} photo(s) with matching faces`);
      }
    } catch (error) {
      console.error('Face search error:', error);
      toast.error('Face search failed');
    } finally {
      setFaceSearching(false);
    }
  };

  const handleWebcamCapture = async (file) => {
    setFaceSearchFile(file);
    setShowWebcam(false);

    // Auto-trigger face search
    setFaceSearching(true);
    try {
      const formData = new FormData();
      formData.append('gallery_id', galleryId);
      formData.append('file', file);

      const response = await axios.post(
        `${backendUrl}/api/search/faces`,
        formData,
        axiosConfig
      );

      const matches = response.data.matches || response.data || [];
      const matchCount = response.data.total || matches.length || 0;

      setFaceSearchResults(matches);

      if (matchCount === 0) {
        toast.error('No matching faces found in this gallery');
      } else {
        toast.success(`Found ${matchCount} photo(s) with matching faces!`);
      }
    } catch (error) {
      console.error('Face search error:', error);
      toast.error('Face search failed. Please try again.');
    } finally {
      setFaceSearching(false);
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(photoId)) {
        newSet.delete(photoId);
      } else {
        newSet.add(photoId);
      }
      return newSet;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedPhotos.size === 0) {
      toast.error('No photos selected');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedPhotos.size} selected photo(s)? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      const photoIds = Array.from(selectedPhotos);
      const deletePromises = photoIds.map(photoId =>
        axios.delete(`${backendUrl}/api/photos/${photoId}`, axiosConfig)
      );

      await Promise.all(deletePromises);

      toast.success(`Successfully deleted ${selectedPhotos.size} photo(s)`);
      setSelectedPhotos(new Set());
      setIsMultiSelectMode(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete some photos');
    }
  };

  const handleBulkMove = async () => {
    if (selectedPhotos.size === 0) {
      toast.error('No photos selected');
      return;
    }

    try {
      await axios.put(
        `${backendUrl}/api/galleries/${galleryId}/photos/set-collection`,
        {
          photo_ids: Array.from(selectedPhotos),
          collection_id: moveTargetCollection || null
        },
        axiosConfig
      );

      const targetName = moveTargetCollection
        ? collections.find(c => c.id === moveTargetCollection)?.name || 'collection'
        : 'Uncategorized';
      toast.success(`Moved ${selectedPhotos.size} photo(s) to "${targetName}"`);
      setSelectedPhotos(new Set());
      setIsMultiSelectMode(false);
      setShowMoveDialog(false);
      setMoveTargetCollection('');
      onRefresh();
    } catch (error) {
      toast.error('Failed to move photos');
    }
  };

  const openLightbox = (photo, index) => {
    if (isMultiSelectMode) {
      togglePhotoSelection(photo.id);
    } else {
      setLightboxPhoto(photo);
      setLightboxIndex(index);
    }
  };

  const closeLightbox = () => {
    setLightboxPhoto(null);
  };

  const navigateLightbox = (direction) => {
    const newIndex = direction === 'next'
      ? (lightboxIndex + 1) % displayPhotos.length
      : (lightboxIndex - 1 + displayPhotos.length) % displayPhotos.length;
    setLightboxIndex(newIndex);
    setLightboxPhoto(displayPhotos[newIndex]);
  };

  const handleDownloadPhoto = async (photo) => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/photos/${photo.id}/download`,
        { ...axiosConfig, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', photo.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started!');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#BF212F] mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading photos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Webcam Dialog */}
      <WebcamCapture
        open={showWebcam}
        onCapture={handleWebcamCapture}
        onClose={() => setShowWebcam(false)}
      />

      {/* Unified Toolbar - Sticky */}
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700 p-3 md:p-4">
        <div className="flex flex-col sm:flex-row gap-2 md:gap-4 items-stretch sm:items-center justify-between">
          {/* Search and Photo Count */}
          <div className="flex items-center gap-2 md:gap-4 flex-1">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search..."
                value={filenameSearch}
                onChange={(e) => setFilenameSearch(e.target.value)}
                className="pl-9 pr-8 h-9 md:h-10 bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 text-sm"
              />
              {filenameSearch && (
                <button
                  onClick={() => setFilenameSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Photo count badge */}
            <div className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 bg-slate-100 dark:bg-slate-700 rounded-full">
              <ImageIcon className="w-3 h-3 md:w-4 md:h-4 text-slate-500" />
              <span className="text-xs md:text-sm font-semibold text-slate-700 dark:text-slate-200">
                {(totalPhotoCount || displayPhotos.length).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {/* Collection filter */}
            {collections.length > 0 && (
              <select
                value={collectionFilter}
                onChange={(e) => onCollectionFilterChange(e.target.value)}
                className="h-8 md:h-9 px-2 md:px-3 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-xs md:text-sm font-medium text-slate-700 dark:text-slate-200 cursor-pointer hover:border-slate-300 dark:hover:border-slate-500 transition-colors max-w-[140px]"
              >
                <option value="all">All Collections</option>
                <option value="uncategorized">Uncategorized</option>
                {collections.map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.photo_count || 0})</option>
                ))}
              </select>
            )}

            {/* Sort dropdown */}
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="h-8 md:h-9 px-2 md:px-3 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-xs md:text-sm font-medium text-slate-700 dark:text-slate-200 cursor-pointer hover:border-slate-300 dark:hover:border-slate-500 transition-colors"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>

            {/* Multi-select toggle */}
            <Button
              variant={isMultiSelectMode ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setIsMultiSelectMode(!isMultiSelectMode);
                if (isMultiSelectMode) {
                  setSelectedPhotos(new Set());
                }
              }}
              className={`h-8 md:h-9 text-xs md:text-sm ${isMultiSelectMode
                ? 'bg-[#BF212F] text-white border-0'
                : ''
              }`}
            >
              <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">{isMultiSelectMode ? 'Exit Select' : 'Select'}</span>
              <span className="sm:hidden">{isMultiSelectMode ? 'Exit' : 'Select'}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-3 md:px-4 overflow-x-auto">
          <TabsList className="bg-transparent h-auto p-0 gap-2 md:gap-4 flex">
            <TabsTrigger
              value="all"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#BF212F] data-[state=active]:text-[#BF212F] rounded-none px-0 pb-2 md:pb-3 text-xs md:text-sm whitespace-nowrap"
            >
              <ImageIcon className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">All Photos</span>
              <span className="sm:hidden">All</span>
            </TabsTrigger>
            {/* AI tag + face search are admin-only (crew aren't permitted these endpoints) */}
            {!isCrew && (
              <TabsTrigger
                value="tags"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#BF212F] data-[state=active]:text-[#BF212F] rounded-none px-0 pb-2 md:pb-3 text-xs md:text-sm whitespace-nowrap"
              >
                <Sparkles className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                <span className="hidden sm:inline">AI Search</span>
                <span className="sm:hidden">AI</span>
              </TabsTrigger>
            )}
            {!isCrew && (
              <TabsTrigger
                value="faces"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#BF212F] data-[state=active]:text-[#BF212F] rounded-none px-0 pb-2 md:pb-3 text-xs md:text-sm whitespace-nowrap"
              >
                <User className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                <span className="hidden sm:inline">Face Search</span>
                <span className="sm:hidden">Faces</span>
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* Tab Content */}
        <TabsContent value="all" className="flex-1 overflow-auto p-2 md:p-4 mt-0">
          <PhotoGrid
            photos={displayPhotos}
            isMultiSelectMode={isMultiSelectMode}
            selectedPhotos={selectedPhotos}
            onPhotoClick={openLightbox}
            onToggleSelection={togglePhotoSelection}
            onViewTags={setSelectedPhotoTags}
            onDownload={handleDownloadPhoto}
            onSetFeatured={isCrew ? null : onSetFeatured}
            onDelete={onDeletePhoto}
            backendUrl={backendUrl}
          />

          {/* Load More */}
          {hasMore && displayPhotos.length > 0 && (
            <div className="mt-8 text-center">
              {loadingMore ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#BF212F]"></div>
                  <span className="ml-3 text-slate-600">Loading more...</span>
                </div>
              ) : (
                <Button
                  onClick={loadMorePhotos}
                  variant="outline"
                  className="border-[#BF212F] text-[#BF212F] hover:bg-[#BF212F] hover:text-white"
                >
                  Load More Photos
                </Button>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tags" className="flex-1 overflow-auto p-2 md:p-4 mt-0">
          {/* AI Tag Search Interface */}
          <Card className="mb-6 p-4">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
              Search by AI Tags
            </h3>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-4">
              {['all', 'facial_attributes', 'emotions', 'activities', 'people_groups', 'event_scenes', 'event_objects', 'atmosphere'].map(cat => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className={selectedCategory === cat ? 'bg-[#BF212F]' : ''}
                >
                  {cat === 'all' ? 'All' : cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Button>
              ))}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-3 border rounded-lg bg-slate-50 dark:bg-slate-700 mb-4">
              {(selectedCategory === 'all' ? allTags : categorizedTags[selectedCategory] || []).map(tag => (
                <Badge
                  key={tag}
                  variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                  className="cursor-pointer transition-all hover:scale-105"
                  style={selectedTags.includes(tag) ? { backgroundColor: '#BF212F' } : {}}
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Search Button */}
            <div className="flex items-center gap-4">
              <Button
                onClick={handleSearchByTags}
                disabled={selectedTags.length === 0}
                className="bg-[#BF212F] hover:bg-[#8B1923]"
              >
                <Search className="w-4 h-4 mr-2" />
                Search {selectedTags.length > 0 && `(${selectedTags.length} tags)`}
              </Button>

              {selectedTags.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedTags([]);
                    setSearchResults([]);
                  }}
                >
                  Clear Tags
                </Button>
              )}
            </div>
          </Card>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <PhotoGrid
              photos={searchResults}
              isMultiSelectMode={isMultiSelectMode}
              selectedPhotos={selectedPhotos}
              onPhotoClick={openLightbox}
              onToggleSelection={togglePhotoSelection}
              onViewTags={setSelectedPhotoTags}
              onDownload={handleDownloadPhoto}
              onSetFeatured={isCrew ? null : onSetFeatured}
              onDelete={onDeletePhoto}
              backendUrl={backendUrl}
            />
          )}
        </TabsContent>

        <TabsContent value="faces" className="flex-1 overflow-auto p-2 md:p-4 mt-0">
          {/* Face Search Interface */}
          <Card className="mb-6 p-4">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
              Search by Face
            </h3>

            {/* Reference Photo Preview */}
            {faceSearchFile && (
              <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200">
                <div className="flex items-center gap-4">
                  <img
                    src={URL.createObjectURL(faceSearchFile)}
                    alt="Reference"
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div>
                    <p className="font-medium text-slate-800 dark:text-white">Reference photo selected</p>
                    <button
                      onClick={() => setFaceSearchFile(null)}
                      className="text-xs text-red-600 hover:text-red-700 underline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Upload Options */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Button
                onClick={() => setShowWebcam(true)}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2"
              >
                <Camera className="w-6 h-6 text-purple-600" />
                <span>Take Selfie</span>
              </Button>

              <label className="cursor-pointer block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      setFaceSearchFile(e.target.files[0]);
                    }
                  }}
                  className="hidden"
                />
                <div className="h-auto py-4 w-full flex flex-col items-center gap-2 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                  <Upload className="w-6 h-6 text-pink-600" />
                  <span className="text-sm font-medium">Upload Photo</span>
                </div>
              </label>
            </div>

            {/* Search Button */}
            <Button
              onClick={handleFaceSearch}
              disabled={!faceSearchFile || faceSearching}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {faceSearching ? 'Searching...' : 'Find Matching Photos'}
            </Button>
          </Card>

          {/* Face Search Results */}
          {faceSearchResults.length > 0 && (
            <PhotoGrid
              photos={faceSearchResults.map(r => r.photo || r)}
              isMultiSelectMode={isMultiSelectMode}
              selectedPhotos={selectedPhotos}
              onPhotoClick={openLightbox}
              onToggleSelection={togglePhotoSelection}
              onViewTags={setSelectedPhotoTags}
              onDownload={handleDownloadPhoto}
              onSetFeatured={isCrew ? null : onSetFeatured}
              onDelete={onDeletePhoto}
              backendUrl={backendUrl}
              showSimilarity={true}
              faceResults={faceSearchResults}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Floating Selection Bar */}
      {isMultiSelectMode && selectedPhotos.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 duration-300">
          <div className="flex items-center gap-4 px-6 py-3 bg-slate-900 dark:bg-slate-800 text-white rounded-2xl shadow-2xl border border-slate-700">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#BF212F] rounded-lg flex items-center justify-center font-bold">
                {selectedPhotos.size}
              </div>
              <span className="font-medium">selected</span>
            </div>

            <div className="h-6 w-px bg-slate-600" />

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // Select all visible photos
                  const allPhotoIds = displayPhotos.map(p => p.id);
                  setSelectedPhotos(new Set(allPhotoIds));
                }}
                className="text-white hover:bg-slate-700"
              >
                Select All
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={handleBulkDelete}
                className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>

              {!isCrew && collections.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowMoveDialog(true)}
                  className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Move
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedPhotos(new Set());
                  setIsMultiSelectMode(false);
                }}
                className="text-slate-400 hover:text-white hover:bg-slate-700"
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightboxPhoto} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-[95vw] w-auto h-auto max-h-[95vh] p-0 bg-white dark:bg-slate-900 overflow-hidden" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Photo Lightbox</DialogTitle>
          {lightboxPhoto && (
            <div className="relative flex flex-col items-center justify-center">
              {/* Controls */}
              <div className="absolute top-4 left-4 z-10 flex gap-2">
                <Button
                  onClick={() => handleDownloadPhoto(lightboxPhoto)}
                  className="bg-white/90 text-slate-700 hover:bg-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>

              {/* Image */}
              <div className="flex items-center justify-center p-4">
                <img
                  src={lightboxPhoto.preview_url || (lightboxPhoto.preview_path ? `${backendUrl}/api/photos/file/previews/${lightboxPhoto.preview_path.split('/').pop()}` : '')}
                  alt={lightboxPhoto.filename}
                  className="max-w-[90vw] max-h-[85vh] object-contain"
                />
              </div>

              {/* Navigation */}
              <Button
                onClick={() => navigateLightbox('prev')}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 text-slate-700 hover:bg-white rounded-full w-12 h-12"
              >
                ‹
              </Button>
              <Button
                onClick={() => navigateLightbox('next')}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 text-slate-700 hover:bg-white rounded-full w-12 h-12"
              >
                ›
              </Button>

              {/* Filename */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                <p className="text-sm text-slate-600 dark:text-slate-300 bg-white/90 dark:bg-slate-800/90 px-4 py-2 rounded-full">
                  {lightboxPhoto.filename}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Tags Modal */}
      <Dialog open={!!selectedPhotoTags} onOpenChange={() => setSelectedPhotoTags(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#BF212F]" />
              AI Tags
            </DialogTitle>
            <DialogDescription>
              {selectedPhotoTags?.filename}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-2 p-4 border rounded-lg bg-slate-50 dark:bg-slate-700">
            {selectedPhotoTags?.tags.map((tag, index) => (
              <Badge key={index} variant="outline" className="bg-white">
                {tag}
              </Badge>
            ))}
          </div>

          <DialogFooter>
            <Button onClick={() => setSelectedPhotoTags(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to Collection Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move to Collection</DialogTitle>
            <DialogDescription>
              Move {selectedPhotos.size} selected photo(s) to a collection.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <select
              value={moveTargetCollection}
              onChange={(e) => setMoveTargetCollection(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              <option value="">Uncategorized (No Collection)</option>
              {collections.map(c => (
                <option key={c.id} value={c.id}>{c.name} ({c.photo_count || 0} photos)</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoveDialog(false)}>Cancel</Button>
            <Button
              onClick={handleBulkMove}
              className="bg-[#BF212F] text-white"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Move Photos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Photo Grid Component
const PhotoGrid = ({
  photos,
  isMultiSelectMode,
  selectedPhotos,
  onPhotoClick,
  onToggleSelection,
  onViewTags,
  onDownload,
  onSetFeatured,
  onDelete,
  backendUrl,
  showSimilarity = false,
  faceResults = []
}) => {
  if (photos.length === 0) {
    return (
      <div className="text-center py-20 bg-slate-50 dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700">
        <ImageIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <p className="text-slate-600 dark:text-slate-400 text-lg font-medium mb-2">
          No photos found
        </p>
        <p className="text-slate-500 dark:text-slate-500 text-sm">
          Try adjusting your search or filters
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
      {photos.map((photo, index) => {
        const similarity = showSimilarity && faceResults.find(r => (r.photo?.id || r.id) === photo.id)?.similarity;

        return (
          <div
            key={photo.id}
            className="group relative aspect-square bg-slate-100 dark:bg-slate-700 rounded-xl overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#BF212F] hover:shadow-xl transition-all duration-200"
          >
            <div onClick={() => onPhotoClick(photo, index)} className="w-full h-full">
              <img
                src={photo.thumbnail_url || (photo.thumbnail_path ? `${backendUrl}/api/photos/file/thumbnails/${photo.thumbnail_path.split('/').pop()}` : '')}
                alt={photo.filename}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Similarity badge for face search */}
            {similarity && (
              <div className="absolute top-2 left-2 z-10">
                <Badge className="bg-purple-600 text-white text-xs">
                  {similarity.toFixed(1)}% Match
                </Badge>
              </div>
            )}

            {/* AI Tags button */}
            {photo.ai_tags && photo.ai_tags.length > 0 && !isMultiSelectMode && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewTags({ photo_id: photo.id, filename: photo.filename, tags: photo.ai_tags });
                }}
                className="absolute top-2 right-2 z-10 w-8 h-8 bg-white/90 rounded-full shadow-lg flex items-center justify-center hover:bg-white hover:scale-110 transition-all"
              >
                <Sparkles className="w-4 h-4 text-[#BF212F]" />
              </button>
            )}

            {/* Multi-select checkbox */}
            {isMultiSelectMode && (
              <div
                className="absolute top-2 left-2 z-10 w-8 h-8 bg-white/90 rounded-md shadow-lg flex items-center justify-center cursor-pointer border-2 transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelection(photo.id);
                }}
                style={{
                  borderColor: selectedPhotos.has(photo.id) ? '#BF212F' : '#cbd5e1',
                  backgroundColor: selectedPhotos.has(photo.id) ? '#BF212F' : 'rgba(255, 255, 255, 0.9)'
                }}
              >
                {selectedPhotos.has(photo.id) && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
            )}

            {/* Action buttons on hover */}
            {!isMultiSelectMode && (
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(photo);
                    }}
                    className="w-9 h-9 bg-white/95 rounded-lg hover:bg-white shadow-lg flex items-center justify-center hover:scale-110 transition-all"
                    title="Download"
                  >
                    <Download className="w-4 h-4 text-slate-700" />
                  </button>

                  {onSetFeatured && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetFeatured(photo.id);
                      }}
                      className="w-9 h-9 bg-amber-500/95 rounded-lg hover:bg-amber-600 shadow-lg flex items-center justify-center hover:scale-110 transition-all"
                      title="Set as Featured"
                    >
                      <span className="text-white text-sm">⭐</span>
                    </button>
                  )}

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Delete this photo?')) {
                        onDelete(photo.id);
                      }
                    }}
                    className="w-9 h-9 bg-red-500/95 rounded-lg hover:bg-red-600 shadow-lg flex items-center justify-center hover:scale-110 transition-all"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PhotosPanel;
